'use client';

import SectionBadge from '@/app/(website)/components/SectionBadge';
import {ReactNode, useEffect, useRef, useState} from 'react';
import {Heading} from '@/components/heading';
import {Body} from '@/components/body';
import {cn} from '@/lib/utils';

export default function SectionHeader({
  badgeText,
  title,
  subtitle,
  titleAnimationStart = 'bottom',
  className,
}: {
  badgeText: string;
  title: string | ReactNode;
  subtitle: string;
  titleAnimationStart?: 'top' | 'bottom' | 'left' | 'right' | 'none';
  className?: string;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);

  const titleAnimationClasses = {
    start: 'opacity-0 -translate-y-20',
    end: 'opacity-100 translate-y-0',
  };

  if (titleAnimationStart === 'left') {
    titleAnimationClasses.start = 'opacity-0 -translate-x-20';
    titleAnimationClasses.end = 'opacity-100 translate-x-0';
  }

  if (titleAnimationStart === 'right') {
    titleAnimationClasses.start = 'opacity-0 translate-x-20';
    titleAnimationClasses.end = 'opacity-100 translate-x-0';
  }

  if (titleAnimationStart === 'bottom') {
    titleAnimationClasses.start = 'opacity-0 translate-y-20';
    titleAnimationClasses.end = 'opacity-100 translate-y-0';
  }

  if (titleAnimationStart === 'none') {
    titleAnimationClasses.start = 'opacity-100 translate-y-0';
    titleAnimationClasses.end = 'opacity-100 translate-y-0';
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
      },
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      if (titleRef.current) {
        observer.unobserve(titleRef.current);
      }
    };
  }, []);

  return (
    <div
      className={cn(
        'flex flex-col justify-center items-center gap-4',
        className,
      )}
    >
      <div className="flex flex-col items-center gap-8">
        <SectionBadge text={badgeText} />
        <Heading
          variant="h2"
          className={`text-center max-w-[56.625rem] transition-all duration-1000 ease-in-out transform ${
            isVisible ? titleAnimationClasses.end : titleAnimationClasses.start
          }`}
          ref={titleRef}
        >
          {title}
        </Heading>
        <Body
          variant="large"
          className={`text-center max-w-[42rem] transition-opacity duration-1000 delay-200 ease-in-out ${
            isVisible
              ? 'opacity-100'
              : titleAnimationStart === 'none'
                ? 'opacity-100'
                : 'opacity-0'
          }`}
          ref={subtitleRef}
        >
          {subtitle}
        </Body>
      </div>
    </div>
  );
}
